import React, { Fragment, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Globe, Mail, MapPin, Info, X } from "lucide-react";

/**
 * Props:
 * - open: boolean
 * - onClose: () => void
 * - client: object (expects .us_code as userid; change below if yours differs)
 * - fetchProfile: (userid: string) => Promise<ProfileData|null>
 */
export default function WhatsAppProfileModal({ open, onClose, client, fetchProfile }) {
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState(null);

  // Adjust this if your row object uses a different field for userid
  const userid = client?.us_code || client?.userid;

  // Clear state when the modal closes (prevents old flicker)
  useEffect(() => {
    if (!open) {
      setProfile(null);
      setError(null);
      setLoading(false);
    }
  }, [open]);

  // Fetch on open or when userid changes
  useEffect(() => {
    let alive = true;
    const run = async () => {
      if (!open || !userid) return;
      setLoading(true);
      setError(null);
      setProfile(null);
      try {
        const data = await fetchProfile(userid);
        if (!alive) return;
        if (data) {
          setProfile(data);
        } else {
          setError("No profile data found.");
        }
      } catch (e) {
        if (!alive) return;
        setError("Failed to load WhatsApp profile.");
      } finally {
        if (alive) setLoading(false);
      }
    };
    run();
    return () => {
      alive = false;
    };
  }, [open, userid, fetchProfile]);

  return (
    <Transition show={open} as={Fragment}>
      <Dialog onClose={onClose} className="relative z-50">
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-150"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>

        {/* Panel */}
        <div className="fixed inset-0 grid place-items-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-150"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-100"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="w-full max-w-xl overflow-hidden rounded-2xl bg-white shadow-xl">
              {/* WhatsApp-style header */}
              <div className="relative h-28 bg-[#075E54] sm:h-32">
                <button
                  className="absolute right-3 top-3 rounded-full bg-white/10 p-1.5 text-white hover:bg-white/20"
                  onClick={onClose}
                  aria-label="Close"
                >
                  <X size={18} />
                </button>

                <div className="absolute -bottom-8 left-5 z-10 flex items-end gap-3">
                  {/* Avatar */}
                  <div className="h-20 w-20 overflow-hidden rounded-full border-4 border-white bg-white shadow-sm">
                    {profile?.profile_picture_url ? (
                      <img
                        src={profile.profile_picture_url}
                        alt="WA avatar"
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="grid h-full w-full place-items-center text-xs text-gray-500">
                        No Image
                      </div>
                    )}
                  </div>

                  {/* Name + Meta */}
                  <div className="mb-5">
                    <div className="max-w-[calc(100vw-10rem)] sm:max-w-[28rem] text-lg font-semibold leading-tight text-white drop-shadow-sm line-clamp-2">
                      {client?.organization_name || "WhatsApp Business"}
                    </div>
                    <div className="text-xs text-white/90">
                      {profile?.messaging_product ? profile.messaging_product.toUpperCase() : "—"}
                      {profile?.vertical ? ` • ${profile.vertical}` : ""}
                    </div>
                  </div>
                </div>
              </div>

              {/* Body */}
              <div className="px-5 pb-5 pt-16 sm:pt-20">
                {loading ? <LoaderSkeleton /> : null}

                {!loading && error ? (
                  <div className="grid place-items-center py-10 text-sm text-red-600">{error}</div>
                ) : null}

                {!loading && !error && !profile ? (
                  <div className="grid place-items-center py-10 text-sm text-gray-500">
                    No profile data available.
                  </div>
                ) : null}

                {!loading && !error && profile ? (
                  <div className="space-y-4">
                    {/* About */}
                    <Section title="About" icon={<Info size={16} />}>
                      <p className="text-sm text-gray-800">{profile.about || "—"}</p>
                    </Section>

                    {/* Description */}
                    {profile.description ? (
                      <Section title="Description" icon={<Info size={16} />}>
                        <p className="text-sm leading-relaxed text-gray-800">{profile.description}</p>
                      </Section>
                    ) : null}

                    {/* Contact grid */}
                    <div className="grid gap-4 sm:grid-cols-2">
                      <Section title="Email" icon={<Mail size={16} />}>
                        <p className="text-sm text-gray-800">{profile.email || "—"}</p>
                      </Section>
                      <Section title="Address" icon={<MapPin size={16} />}>
                        <p className="text-sm text-gray-800">{profile.address || "—"}</p>
                      </Section>
                    </div>

                    {/* Websites */}
                    <Section title="Websites" icon={<Globe size={16} />}>
                      <div className="flex flex-wrap gap-2">
                        {Array.isArray(profile.websites) && profile.websites.length > 0 ? (
                          profile.websites.map((w) => (
                            <a
                              key={w}
                              className="inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs text-[#075E54] hover:bg-[#075E54]/5"
                              href={w}
                              target="_blank"
                              rel="noreferrer"
                              title={w}
                            >
                              <Globe size={12} /> {w.replace(/^https?:\/\//, "")}
                            </a>
                          ))
                        ) : (
                          <span className="text-sm text-gray-800">—</span>
                        )}
                      </div>
                    </Section>
                  </div>
                ) : null}
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}

function Section({ title, icon, children }) {
  return (
    <div className="rounded-2xl border p-3">
      <div className="mb-1 flex items-center gap-2 text-xs font-semibold text-gray-500">
        <span className="text-[#075E54]">{icon}</span>
        {title}
      </div>
      {children}
    </div>
  );
}

export function LoaderSkeleton() {
  return (
    <div className="space-y-4">
      {/* Top row skeletons */}
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="rounded-2xl border p-3">
          <div className="mb-1 h-3 w-20 animate-pulse rounded bg-gray-200" />
          <div className="h-4 w-40 animate-pulse rounded bg-gray-200" />
        </div>
        <div className="rounded-2xl border p-3">
          <div className="mb-1 h-3 w-24 animate-pulse rounded bg-gray-200" />
          <div className="h-4 w-32 animate-pulse rounded bg-gray-200" />
        </div>
      </div>

      {/* Description skeleton */}
      <div className="rounded-2xl border p-3">
        <div className="mb-1 h-3 w-24 animate-pulse rounded bg-gray-200" />
        <div className="space-y-2">
          <div className="h-3 w-full animate-pulse rounded bg-gray-200" />
          <div className="h-3 w-11/12 animate-pulse rounded bg-gray-200" />
          <div className="h-3 w-9/12 animate-pulse rounded bg-gray-200" />
        </div>
      </div>

      {/* Websites skeleton */}
      <div className="rounded-2xl border p-3">
        <div className="mb-1 h-3 w-20 animate-pulse rounded bg-gray-200" />
        <div className="flex gap-2">
          <div className="h-6 w-24 animate-pulse rounded-full border bg-gray-100" />
          <div className="h-6 w-28 animate-pulse rounded-full border bg-gray-100" />
          <div className="h-6 w-20 animate-pulse rounded-full border bg-gray-100" />
        </div>
      </div>
    </div>
  );
}
